import { useCallback, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { round } from 'utils/number';
import { DisplayHelpers } from 'utils/helpers';
import {
  getInheritedSettings,
  getClassificationFromSource,
  calculateNewTotalMarginPercentW2,
  resolveW2BurdenMultipliers,
} from '../PositionRateSettingsForm.utils';
import {
  CONTROLLABLE_CODES,
  CONTROLLED_RATE_FIELDS_KEY,
  CONTROLLER_WRITTEN_FIELDS,
  DEFAULT_CONTROLLED,
  RATE_CONTROLLER_ENGAGED_KEY,
  baseMarginField,
  codeToField,
  deriveBlendedMarkup,
  markupFactorOf,
  solveRates,
  swapControlled,
  type RateCode,
  type RateValues,
} from './rateControllerModel';
import { calculatedFieldPath } from 'resources/rate_settings/utils/fieldsVisibility';
import type { RateControllerValue } from './RateControllerContext';

type UseRateControllerProps = {
  source: string;
  inheritedSettings: any;
  resource?: string;
};

const num = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const isBlank = (v: unknown): boolean => v === null || v === undefined || v === '';

const toCents = (value: string | number): number =>
  typeof value === 'string' ? DisplayHelpers.stringToCents(value) : value;

export const useRateController = ({
  source,
  inheritedSettings,
  resource,
}: UseRateControllerProps): RateControllerValue => {
  const { watch, getValues, setValue, resetField, getFieldState } = useFormContext();
  const controlledKey = `${source}.${CONTROLLED_RATE_FIELDS_KEY}`;
  const engagedKey = `${source}.${RATE_CONTROLLER_ENGAGED_KEY}`;
  const { level, tab } = getClassificationFromSource(source);
  const isLPRS = resource === 'LocationPositionRateSetting';
  // Location-level settings have no separate 12hr rate (the blended fields are
  // hidden there) — the 12hr structure never re-bases their calculations.
  const isLocationLevel = isLPRS || resource === 'AgencyLocationPositionRateSetting';
  const isW2 = source.includes('w2');

  const watchedControlled = watch(controlledKey);
  const controlled: RateCode[] = useMemo(
    () =>
      Array.isArray(watchedControlled) && watchedControlled.length === 2
        ? (watchedControlled as RateCode[])
        : DEFAULT_CONTROLLED,
    [watchedControlled],
  );

  const recompute = useCallback(() => {
    // Ensure the selection is initialized (reactive to form resets, e.g. react-admin).
    const currentControlled = getValues(controlledKey);
    if (!Array.isArray(currentControlled) || currentControlled.length !== 2) {
      setValue(controlledKey, DEFAULT_CONTROLLED, { shouldDirty: false });
    }

    if (getValues(engagedKey) !== true) {
      return;
    }

    const merged = getInheritedSettings({ source, inheritedSettings, watch });
    const marginPath = baseMarginField(source);

    // operational_markup is W2-only: force 0 on ten99 even if stale data carries a value.
    const markups = {
      invoicingMarkup: num(merged.invoicing_markup),
      miscMarkup: num(merged.misc_markup),
      operationalMarkup: isW2 ? num(merged.operational_markup) : 0,
    };

    // charge_rate_cents lives under the settings namespace (calculatedFieldPath),
    // outside the own-blob hash that getInheritedSettings merges — read the live
    // form value first so admin edits are seen, then fall back to the merge.
    const liveChargeRate = getValues(codeToField('chargeRate', source, getValues));
    const values: RateValues = {
      professionalRate: num(merged.pro_rate_cents),
      baseMargin: num(merged?.[marginPath.split('.').pop() as string]),
      baseChargeRate: num(merged.base_charge_rate_cents),
      chargeRate: isBlank(liveChargeRate) ? num(merged.charge_rate_cents) : num(liveChargeRate),
    };

    const rawBlendedMultiplier = merged?.blended_rates?.blended_rate_markup;
    const blendedFactor =
      merged?.use_blended_rate && !isLocationLevel ? 1 + num(rawBlendedMultiplier) : 1;

    const isW2Local = tab === 'w2.local';
    const burdenDivisor = isW2Local ? 1 + resolveW2BurdenMultipliers(watch) : 1;

    const solved = solveRates({
      controlled,
      values,
      markups,
      blendedFactor,
      burdenDivisor,
      marginOnChargeRate: isW2Local,
    });

    const writeIfChanged = (path: string, next: number) => {
      if (!Number.isFinite(next)) {
        return;
      }
      // Write when the field is empty (so a legitimate 0 fills a null) or when
      // its numeric value differs. num() keeps string-typed margins (e.g. "0.35")
      // from churning against their numeric equivalent. shouldDirty stays false:
      // these are system-computed refreshes, not admin edits — a pristine form
      // must not become saveable (or persist inherited snapshots) just by mounting.
      const current = getValues(path);
      if (isBlank(current) || num(current) !== next) {
        setValue(path, next, { shouldDirty: false });
      }
    };

    const computed = CONTROLLABLE_CODES.filter((code) => !controlled.includes(code));
    computed.forEach((code) => {
      const path = codeToField(code, source, getValues);
      if (code === 'chargeRate' || code === 'professionalRate') {
        writeIfChanged(path, Math.round(solved[code]));
      } else if (code === 'baseChargeRate') {
        writeIfChanged(path, round(solved.baseChargeRate, 4));
      } else {
        writeIfChanged(path, round(solved.baseMargin, 5));
      }
    });

    const chargeMarkupFactor = markupFactorOf(markups);
    const finalMarginOf = (baseMargin: number) =>
      round(1 - (1 - baseMargin) / chargeMarkupFactor, 5);
    const hasBaseMargin = controlled.includes('baseMargin')
      ? !isBlank(merged?.[marginPath.split('.').pop() as string])
      : Number.isFinite(solved.baseMargin);
    if (tab === 'ten99' && hasBaseMargin) {
      writeIfChanged(
        calculatedFieldPath(source, 'margin_percent_1099', getValues),
        finalMarginOf(solved.baseMargin),
      );
    } else if (tab === 'w2.local') {
      calculateNewTotalMarginPercentW2({
        baseW2Margin: solved.baseMargin,
        setValue,
        source,
        watch,
      });
    } else if (tab === 'w2.travel') {
      if (hasBaseMargin) {
        writeIfChanged(
          calculatedFieldPath(source, 'margin_percent_w2_travel', getValues),
          finalMarginOf(solved.baseMargin),
        );
      }
      // TCV mirrors the travel margin but from its own (uncontrolled) base field.
      const baseMarginTcv = merged?.base_margin_percent_w2_tcv;
      if (!isBlank(baseMarginTcv)) {
        writeIfChanged(
          calculatedFieldPath(source, 'margin_percent_w2_tcv', getValues),
          finalMarginOf(num(baseMarginTcv)),
        );
      }
    }

    // Blank/NaN means "no markup configured anywhere" — leave blended_rate_cents
    // alone. An explicit 0 is a real value and must sync blended to the charge rate.
    const rawBlendedMarkup = merged?.blended_rates?.blended_rate_markup;
    if (!isBlank(rawBlendedMarkup) && Number.isFinite(Number(rawBlendedMarkup))) {
      writeIfChanged(
        `${source}.blended_rate_cents`,
        Math.round(solved.chargeRate * (1 + num(rawBlendedMarkup))),
      );
    }

    if (controlled.includes('chargeRate') && isLPRS) {
      const markupFields = [
        'invoicing_markup',
        'misc_markup',
        ...(isW2 ? ['operational_markup'] : []),
      ];
      markupFields.forEach((mf) => {
        if (isBlank(getValues(`${source}.${mf}`)) && !isBlank(merged?.[mf])) {
          setValue(`${source}.${mf}`, num(merged[mf]), { shouldDirty: false });
        }
      });
    }
  }, [
    controlled,
    source,
    tab,
    isLPRS,
    isLocationLevel,
    isW2,
    inheritedSettings,
    watch,
    getValues,
    setValue,
    controlledKey,
    engagedKey,
  ]);

  // Re-solve when the selection changes (or on first mount). setTimeout defers the
  // writes past react-admin's form initialization reset, which would otherwise
  // silently swallow them (the reset does not re-fire this effect).
  useEffect(() => {
    const timeout = setTimeout(recompute, 0);
    return () => clearTimeout(timeout);
  }, [recompute]);

  useEffect(() => {
    const triggers = new Set<string>([
      ...controlled.map((c) => codeToField(c, source, getValues)),
      `${source}.invoicing_markup`,
      `${source}.misc_markup`,
      `${source}.operational_markup`,
      `${source}.blended_rates.blended_rate_markup`,
      `${source}.use_blended_rate`,
      `${source}.base_margin_percent_w2_tcv`,
    ]);
    // Engagement persists only while some admin-typable field holds a value the
    // admin actually entered: dirty (controller writes use shouldDirty:false, so
    // they never count) AND non-blank (a cleared field may stay RHF-dirty when
    // its default was undefined, but it no longer carries admin input).
    const adminInputFields = [
      ...CONTROLLABLE_CODES.map((code) => codeToField(code, source, getValues)),
      `${source}.invoicing_markup`,
      `${source}.misc_markup`,
      `${source}.operational_markup`,
      `${source}.blended_rates.blended_rate_markup`,
      `${source}.use_blended_rate`,
      `${source}.base_margin_percent_w2_tcv`,
    ];
    const sub = watch((_, { name, type }) => {
      if (!name || !triggers.has(name)) {
        return;
      }
      if (type === 'change') {
        const hasAdminInput = adminInputFields.some(
          (path) => !isBlank(getValues(path)) && getFieldState(path).isDirty,
        );
        if (hasAdminInput) {
          if (getValues(engagedKey) !== true) {
            setValue(engagedKey, true, { shouldDirty: false });
          }
        } else if (getValues(engagedKey) === true) {
          setValue(engagedKey, false, { shouldDirty: false });
          CONTROLLER_WRITTEN_FIELDS.forEach((field) =>
            resetField(calculatedFieldPath(source, field, getValues)),
          );
          return;
        }
      }
      recompute();
    });
    return () => sub.unsubscribe();
  }, [
    watch,
    controlled,
    source,
    recompute,
    engagedKey,
    getValues,
    setValue,
    resetField,
    getFieldState,
  ]);

  const isControlled = useCallback((code: RateCode) => controlled.includes(code), [controlled]);

  const toggle = useCallback(
    (code: RateCode) => {
      setValue(engagedKey, true, { shouldDirty: false });
      const updated = swapControlled(controlled, code);
      if (updated !== controlled) {
        setValue(controlledKey, updated, { shouldDirty: false });
      }
    },
    [controlled, controlledKey, engagedKey, setValue],
  );

  const setRateStructure = useCallback(
    (useBlended: boolean) => {
      setValue(engagedKey, true, { shouldDirty: false });
      setValue(`${source}.use_blended_rate`, useBlended, { shouldDirty: true });
      if (isW2) {
        const sibling = tab === 'w2.local' ? 'travel' : 'local';
        const siblingSource = `${level}.w2.${sibling}`;
        setValue(`${siblingSource}.use_blended_rate`, useBlended, { shouldDirty: true });
        setValue(`${siblingSource}.${RATE_CONTROLLER_ENGAGED_KEY}`, true, { shouldDirty: false });
      }
    },
    [setValue, engagedKey, source, isW2, tab, level],
  );

  const setBlendedRate = useCallback(
    (value: string | number) => {
      const merged = getInheritedSettings({ source, inheritedSettings, watch });
      const chargeCents =
        num(getValues(codeToField('chargeRate', source, getValues))) ||
        num(merged.charge_rate_cents);
      const markup = deriveBlendedMarkup(toCents(value), chargeCents);
      if (markup === null) {
        return;
      }
      setValue(engagedKey, true, { shouldDirty: false });
      setValue(`${source}.blended_rates.blended_rate_markup`, markup, { shouldDirty: true });
    },
    [source, inheritedSettings, watch, getValues, setValue, engagedKey],
  );

  const marginVal = num(watch(baseMarginField(source)));
  const chargeRateMarkups = [
    num(watch(`${source}.invoicing_markup`)),
    num(watch(`${source}.misc_markup`)),
    isW2 ? num(watch(`${source}.operational_markup`)) : 0,
  ];
  const hasInvalidMarkup = chargeRateMarkups.some((m) => 1 + m <= 0);

  const warnings = useMemo<string[]>(() => {
    const msgs: string[] = [];
    if (marginVal >= 1) {
      msgs.push('Base Margin must be less than 1.');
    }
    if (hasInvalidMarkup) {
      msgs.push('Each markup must be greater than −1.');
    }
    return msgs;
  }, [marginVal, hasInvalidMarkup]);

  return { controlled, isControlled, toggle, setRateStructure, setBlendedRate, warnings };
};
